import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MUC_DO, DANH_GIA_SLIDE, MUC_UU_TIEN, KET_QUA_TIEU_CHI, KET_QUA_SUA, demMuc, chuanHoaNhanXet, soSanhLuot } from "@shared/nhanXetDeck.js";
import { diemTrenSlide } from "@shared/viTriChu.js";
import SlideChuThich, { AnhNho, useAnhSlide } from "./SlideChuThich.jsx";

/**
 * Báo cáo nhận xét pitch deck. Dùng chung cho trang founder và phần "Xem như
 * founder" trong trang quản trị.
 *
 * Thứ tự đọc có chủ ý (bảng tổng quan trước, chi tiết sau): founder bận, phải
 * biết ngay deck đang ở đâu và sửa gì trước, không phải đọc hết mới thấy giá
 * trị. Chi tiết từng slide thu gọn, bấm mới mở. Không ghim thứ gì khi cuộn:
 * khối ghim che mất nội dung trên màn hình nhỏ.
 *
 * Bản nhận xét cũ chưa có các trường mới (diemDung, checklist, hienTai…) vẫn
 * hiển thị được: khối nào thiếu dữ liệu thì không hiện.
 *
 * Từng slide: ảnh chính slide đó (yc.hinhSlide, hoặc `hinh` ở trang quản trị),
 * chỗ cần sửa được tô, gạch chân và có mũi tên tới thẻ nhận xét cùng số.
 * Nhiều lượt: `cacLuot` (mọi lượt của cùng deck) hiện thanh chọn lượt, `truoc`
 * (lượt trước có kết quả) hiện khối so sánh.
 */
const MUC_CO = { tot: "LOW", "can-sua": "MEDIUM", "nen-bo": "MEDIUM", "nghiem-trong": "HIGH" };
const soDau = (s) => String(s || "").match(/\d+/)?.[0];

function moSlide(e, so) {
  const el = document.getElementById(`slide-${so}`);
  if (!el) return;
  e.preventDefault();
  el.open = true;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function LinkSlide({ slide }) {
  const so = soDau(slide);
  if (!so) return null;
  return <a className="bc-link-slide" href={`#slide-${so}`} onClick={(e) => moSlide(e, so)}>Slide {slide} →</a>;
}

function Thanh({ diem }) {
  return (
    <span className="bc-thanh" aria-hidden="true">
      {[1, 2, 3, 4, 5].map((i) => <span key={i} className={i <= diem ? `day d${diem}` : ""} />)}
    </span>
  );
}

export default function KetQuaNhanXet({ nx: nxVao, yc, hinh = null, cacLuot = [], truoc = null, linkLuot = (id) => `/nhan-xet-deck?xem=${id}` }) {
  const goc = useRef(null);
  // Chuẩn hoá lúc hiển thị: bản đã gửi trước đây vẫn có ngoặc « » thì đổi sang " ".
  const nx = useMemo(() => (nxVao ? chuanHoaNhanXet(nxVao) : null), [nxVao]);
  const trang = hinh || yc?.hinhSlide || null;
  const url = useAnhSlide(trang, Boolean(yc?.deckDaXoa));
  const hinhTheoSo = useMemo(() => new Map((trang || []).map((t) => [t.so, t])), [trang]);
  // In hoặc lưu PDF: mở hết các slide đang thu gọn để bản in đủ nội dung.
  useEffect(() => {
    const mo = () => goc.current?.querySelectorAll("details").forEach((d) => { d.dataset.daMo = d.open ? "1" : ""; d.open = true; });
    const dong = () => goc.current?.querySelectorAll("details").forEach((d) => { d.open = d.dataset.daMo === "1"; });
    window.addEventListener("beforeprint", mo);
    window.addEventListener("afterprint", dong);
    return () => { window.removeEventListener("beforeprint", mo); window.removeEventListener("afterprint", dong); };
  }, []);
  if (!nx) return null;

  const dem = demMuc(nx.slides);
  const truc = (nx.truc || []).filter((t) => t.diem != null);
  const truocSau = (nx.slides || []).filter((s) => s.hienTai && s.vietLai).slice(0, 3);
  const ck = nx.checklist || [];
  const datCk = ck.filter((x) => x.ketQua === "dat").length;
  const ngay = yc?.guiLuc || yc?.createdAt;

  const luotNay = cacLuot.find((y) => y.id === yc?.id);
  return (
    <article className="bc" ref={goc}>
      {cacLuot.length > 1 && (
        <nav className="bc-luot" aria-label="Các lượt nhận xét của deck này">
          <span className="bc-nhan-nho">Các lượt nhận xét</span>
          <ol>
            {cacLuot.map((y) => {
              const d = y.ketQua?.diemTong;
              const nhan = <><strong>Lượt {y.luot}</strong> <span>{y.createdAt ? y.createdAt.toLocaleDateString("vi-VN") : ""}{d != null ? `, ${d} điểm` : y.status !== "da_gui" ? ", chưa có kết quả" : ""}</span></>;
              return <li key={y.id}>{y.id === yc?.id ? <span className="dang" aria-current="page">{nhan}</span> : <Link to={linkLuot(y.id)}>{nhan}</Link>}</li>;
            })}
          </ol>
        </nav>
      )}
      <section className="ket-qua bc-tong" aria-labelledby="bc-diem">
        <div className="bc-tong-trai">
          <p className="bc-nhan">Nhận xét pitch deck{yc?.congTy ? `: ${yc.congTy}` : ""}{luotNay && cacLuot.length > 1 ? `, lượt ${luotNay.luot}` : ""}</p>
          {(yc?.deckTen || ngay) && (
            <p className="bc-meta">{[yc?.deckTen, nx.slides?.length ? `${nx.slides.length} slide` : "", ngay ? `nhận xét ngày ${ngay.toLocaleDateString("vi-VN")}` : ""].filter(Boolean).join(", ")}</p>
          )}
          {nx.diemTong != null && <p className="bc-diem" id="bc-diem">{nx.diemTong}<span>/100</span></p>}
          <p className="bc-ket-luan">{MUC_DO[nx.mucDo] || ""}</p>
          {nx.slides?.length > 0 && (
            <ul className="bc-dem">
              <li className="do"><strong>{dem.nghiemTrong}</strong> slide nghiêm trọng</li>
              <li className="vang"><strong>{dem.canSua}</strong> slide cần sửa</li>
              <li className="xanh"><strong>{dem.on}</strong> slide ổn</li>
            </ul>
          )}
        </div>
        {truc.length > 0 && (
          <div className="bc-tong-phai">
            <p className="bc-nhan">Theo từng phần nhà đầu tư xem</p>
            <ul className="bc-truc">
              {truc.map((t) => (
                <li key={t.ma} title={t.nhanXet}>
                  <span className="bc-truc-ten">{t.ten}</span>
                  <Thanh diem={t.diem} />
                  <span className="bc-truc-so">{t.diem}/5</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <nav className="bc-nhay" aria-label="Đi tới phần">
        {truoc?.ketQua && <a href="#bc-so-sanh">So với lượt trước</a>}
        {nx.uuTien?.length > 0 && <a href="#bc-uu-tien">Việc sửa trước</a>}
        {nx.diemDung?.length > 0 && <a href="#bc-dung">Nhà đầu tư dừng ở đâu</a>}
        {nx.slides?.length > 0 && <a href="#bc-slides">Từng slide</a>}
        {ck.length > 0 && <a href="#bc-checklist">Checklist</a>}
        {nx.cauHoi?.length > 0 && <a href="#bc-cau-hoi">Câu hỏi sẽ gặp</a>}
      </nav>

      {nx.tomTat && (
        <details className="surface bc-nhan-dinh" open={!nx.diemDung?.length}>
          <summary>Nhận định sau 3 phút đọc deck</summary>
          <p>{nx.tomTat}</p>
          {truc.some((t) => t.nhanXet) && (
            <ul>{truc.filter((t) => t.nhanXet).map((t) => <li key={t.ma}><strong>{t.ten}:</strong> {t.nhanXet}</li>)}</ul>
          )}
        </details>
      )}

      {truoc?.ketQua && <SoSanh truoc={truoc} nx={nx} soVoi={nx.soVoiLanTruoc} url={url} yc={yc} linkLuot={linkLuot} />}

      {nx.loiNhan && (
        <section className="surface nx-loi-nhan">
          <h2>Lời nhắn từ fundraising.vn</h2>
          {nx.loiNhan.split(/\n\s*\n/).map((d, i) => <p key={i}>{d}</p>)}
        </section>
      )}

      {(nx.diemDung?.length > 0 || nx.diemManh?.length > 0) && (
        <section className="bc-khoi" id="bc-dung">
          <h2>Nếu là nhà đầu tư, tôi sẽ dừng lại ở đây</h2>
          <div className="bc-dung">
            {nx.diemDung.map((x, i) => (
              <div key={i} className="bc-the do">
                <h3>{x.chuDe}</h3>
                <p>{x.lyDo}</p>
                <LinkSlide slide={x.slide} />
              </div>
            ))}
          </div>
          {nx.diemManh?.length > 0 && (
            <div className="bc-manh">
              <h3>Điều khiến tôi đọc tiếp</h3>
              <ul>{nx.diemManh.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </div>
          )}
        </section>
      )}

      {nx.uuTien?.length > 0 && (
        <section className="bc-khoi" id="bc-uu-tien">
          <h2>Sửa những việc này trước</h2>
          <ol className="bc-uu-tien">
            {nx.uuTien.map((x, i) => (
              <li key={i} className={`bc-the ${x.muc === "vua" ? "vang" : "do"}`}>
                <div className="bc-the-dau">
                  <span className="bc-so">{String(i + 1).padStart(2, "0")}</span>
                  <span className="bc-muc">{MUC_UU_TIEN[x.muc] || MUC_UU_TIEN.cao}</span>
                </div>
                <h3>{x.viec}</h3>
                {x.viSao && <p>{x.viSao}</p>}
                <LinkSlide slide={x.slide} />
              </li>
            ))}
          </ol>
        </section>
      )}

      {truocSau.length > 0 && (
        <section className="bc-khoi">
          <h2>Viết lại thế này</h2>
          <div className="bc-truoc-sau-ds">
            {truocSau.map((s) => (
              <div key={s.so} className="bc-truoc-sau">
                <p className="bc-nhan-nho">Slide {s.so}: hiện tại</p>
                <p className="truoc">{s.hienTai}</p>
                <p className="bc-nhan-nho">Đề xuất</p>
                <p className="sau">{s.vietLai}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {nx.slides?.length > 0 && (
        <section className="bc-khoi" id="bc-slides">
          <h2>Từng slide</h2>
          <p className="nho">Bấm vào một slide để xem chi tiết.{Object.keys(url).length > 0 ? " Chỗ cần sửa được gạch chân trên chính slide của bạn, số trên mũi tên khớp với số của lời nhận xét." : ""}</p>
          <div className="bc-slides">
            {nx.slides.map((s, i) => <SlideMuc key={i} s={s} hinh={hinhTheoSo.get(s.so)} url={url[s.so]} />)}
          </div>
        </section>
      )}

      {nx.thieu?.length > 0 && (
        <section className="surface">
          <h2>Deck còn thiếu</h2>
          <ul>{nx.thieu.map((x, i) => <li key={i}><strong>{x.noiDung}</strong>{x.viSao && `: ${x.viSao}`}</li>)}</ul>
        </section>
      )}

      {ck.length > 0 && (
        <section className="surface" id="bc-checklist">
          <h2>Checklist pitch deck: đạt {datCk}/{ck.length}</h2>
          <p className="nho">Theo đúng <Link to="/cong-cu/pitch-deck-10-slide">checklist 10 slide</Link> của fundraising.vn. Sửa xong, bạn tự chấm lại được ở đó.</p>
          <ul className="bc-ck">
            {ck.map((x) => (
              <li key={x.ma}>
                <span className={`bc-ck-dau ${x.ketQua}`}>{KET_QUA_TIEU_CHI[x.ketQua]}</span>
                <span><strong>{x.ten}</strong>{x.ghiChu && <span className="nho">: {x.ghiChu}</span>}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {nx.cauHoi?.length > 0 && (
        <section className="surface" id="bc-cau-hoi">
          <h2>Câu hỏi nhà đầu tư sẽ hỏi</h2>
          <p className="nho">Chuẩn bị sẵn câu trả lời, hoặc đưa câu trả lời vào deck hay phụ lục.</p>
          <ul>{nx.cauHoi.map((x, i) => <li key={i}>{x}</li>)}</ul>
        </section>
      )}

      {nx.docThem?.length > 0 && (
        <section className="surface">
          <h2>Đọc thêm để sửa nhanh hơn</h2>
          <ul>{nx.docThem.map((x) => <li key={x.path}><Link to={x.path}>{x.ten || x.path}</Link>{x.lyDo && `: ${x.lyDo}`}</li>)}</ul>
        </section>
      )}

      <section className="surface bc-cuoi">
        <h2>Sửa xong rồi?</h2>
        <p>Gửi bản đã sửa để nhận xét lượt tiếp theo. Nhận xét lượt sau sẽ so với lượt này: điểm thay đổi thế nào, việc nào đã sửa xong, và bạn xem lại được cả deck cũ lẫn nhận xét cũ.</p>
        <div className="hang-nut">
          <Link className="nut" to={yc?.id ? `/nhan-xet-deck?luotTiep=${yc.id}#gui-deck` : "/nhan-xet-deck"}>Gửi bản đã sửa{luotNay ? ` (lượt ${luotNay.luot + 1})` : ""}</Link>
          <button type="button" className="nut phu" onClick={() => window.print()}>In hoặc lưu PDF</button>
        </div>
      </section>
    </article>
  );
}

/** Một slide: tóm tắt khi thu gọn, mở ra thì ảnh có chú thích bên trái, thẻ nhận xét bên phải. */
function SlideMuc({ s, hinh, url }) {
  const [chon, setChon] = useState(null);
  const [ghim, setGhim] = useState(null);
  const diem = useMemo(() => diemTrenSlide(s, hinh?.chu), [s, hinh]);
  const dungCu = !s.diem?.length; // bản nhận xét trước khi có chú thích trên ảnh
  const dangChon = ghim ?? chon;
  const doiChon = (i, bam) => (bam ? setGhim((g) => (g === i ? null : i)) : setChon(i));
  const coNoiDung = diem.length > 0 || s.vanDe?.length > 0 || s.goiY?.length > 0 || s.vietLai;
  return (
    <details id={`slide-${s.so}`} className={`bc-slide co ${MUC_CO[s.danhGia] || "MEDIUM"}`}>
      <summary>
        {url && <AnhNho url={url} so={s.so} tiLe={hinh?.tiLe} />}
        <span className="bc-slide-chu">
          <span className="bc-slide-dau"><strong>Slide {s.so}{s.tieuDe ? `: ${s.tieuDe}` : ""}</strong> <span className={`pill bc-pill-${s.danhGia}`}>{DANH_GIA_SLIDE[s.danhGia]}</span></span>
          {(s.nhanXetNgan || s.vanDe?.[0]) && <span className="bc-slide-ngan">{s.nhanXetNgan || s.vanDe[0]}</span>}
          {diem.length > 0 && <span className="bc-slide-dem">{diem.length} chỗ cần sửa</span>}
        </span>
      </summary>
      {coNoiDung && (
        <div className={`bc-slide-than${url ? " co-anh" : ""}`}>
          {url && <SlideChuThich url={url} tiLe={hinh?.tiLe} diem={diem} chu={hinh?.chu} so={s.so} chon={dangChon} onChon={doiChon} />}
          <div className="bc-slide-nx">
            {diem.length > 0 && (
              <ol className="bc-diem-ds">
                {diem.map((d, j) => (
                  <li key={j} className={`bc-diem-the${dangChon === j ? " chon" : ""}`}
                    onMouseEnter={() => setChon(j)} onMouseLeave={() => setChon(null)}>
                    <span className="bc-diem-so" aria-hidden="true">{j + 1}</span>
                    <div>
                      <p className="bc-trich">{d.trich ? <><span className="bc-nhan-nho">Chỗ cần sửa</span><span className="gach">"{d.trich}"</span></> : <span className="bc-nhan-nho">Cả slide</span>}</p>
                      {d.viSao && <><p className="bc-nhan-nho">Vì sao cần sửa</p><p>{d.viSao}</p></>}
                      {d.suaGi && <><p className="bc-nhan-nho">Sửa cái gì</p><p>{d.suaGi}</p></>}
                      {d.cachSua && <><p className="bc-nhan-nho">{dungCu ? "Gợi ý viết lại" : "Sửa như thế nào"}</p><p className="bc-cach">{d.cachSua}</p></>}
                    </div>
                  </li>
                ))}
              </ol>
            )}
            {s.vanDe?.length > 0 && <><p className="bc-nhan-nho">{diem.length && !dungCu ? "Nhận xét thêm" : "Nhà đầu tư thấy gì"}</p><ul>{s.vanDe.map((v, j) => <li key={j}>{v}</li>)}</ul></>}
            {s.goiY?.length > 0 && <><p className="bc-nhan-nho">Nên sửa</p><ul>{s.goiY.map((v, j) => <li key={j}>{v}</li>)}</ul></>}
            {s.vietLai && !dungCu && (
              <div className="bc-truoc-sau">
                {s.hienTai && <><p className="bc-nhan-nho">Câu chính hiện tại</p><p className="truoc">{s.hienTai}</p></>}
                <p className="bc-nhan-nho">Gợi ý viết lại</p>
                <p className="sau">{s.vietLai}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </details>
  );
}

const chenh = (a, b) => (a == null || b == null ? "" : b - a > 0 ? `+${b - a}` : `${b - a}`);

/** So lượt này với lượt trước: điểm, từng phần, việc đã sửa, từng slide hai lượt cạnh nhau. */
function SoSanh({ truoc, nx, soVoi, url, yc, linkLuot }) {
  const cu = useMemo(() => chuanHoaNhanXet(truoc.ketQua), [truoc]);
  const ss = useMemo(() => soSanhLuot(cu, nx), [cu, nx]);
  const urlCu = useAnhSlide(truoc.hinhSlide, Boolean(truoc.deckDaXoa));
  if (!ss) return null;
  const tang = ss.diem.truoc != null && ss.diem.sau != null ? ss.diem.sau - ss.diem.truoc : null;
  const ngay = truoc.createdAt?.toLocaleDateString?.("vi-VN") || "";
  return (
    <section className="bc-khoi bc-so-sanh" id="bc-so-sanh">
      <h2>So với lượt {truoc.luot || "trước"}{ngay ? ` (${ngay})` : ""}</h2>
      <div className="bc-ss-tong">
        <div className="bc-ss-diem">
          <span className="cu">{ss.diem.truoc ?? "?"}</span>
          <span className="mui" aria-hidden="true">→</span>
          <span className="moi">{ss.diem.sau ?? "?"}</span>
          {tang != null && <span className={`bc-ss-chenh ${tang > 0 ? "len" : tang < 0 ? "xuong" : ""}`}>{tang > 0 ? `+${tang}` : tang} điểm</span>}
        </div>
        <ul className="bc-ss-dem">
          <li className="do">Nghiêm trọng: {ss.dem.truoc.nghiemTrong} → <strong>{ss.dem.sau.nghiemTrong}</strong></li>
          <li className="vang">Cần sửa: {ss.dem.truoc.canSua} → <strong>{ss.dem.sau.canSua}</strong></li>
          <li className="xanh">Ổn: {ss.dem.truoc.on} → <strong>{ss.dem.sau.on}</strong></li>
        </ul>
      </div>
      {soVoi?.nhanXet && <p className="bc-ss-nx">{soVoi.nhanXet}</p>}
      {soVoi?.viec?.length > 0 && (
        <>
          <h3>Việc lượt trước đề nghị sửa</h3>
          <ul className="bc-ss-viec">
            {soVoi.viec.map((x, i) => (
              <li key={i}><span className={`bc-ss-kq ${x.ketQua}`}>{KET_QUA_SUA[x.ketQua]}</span><span><strong>{x.viec}</strong>{x.ghiChu && <span className="nho"> {x.ghiChu}</span>}</span></li>
            ))}
          </ul>
        </>
      )}
      {ss.truc.some((t) => t.truoc != null && t.sau != null) && (
        <>
          <h3>Theo từng phần nhà đầu tư xem</h3>
          <ul className="bc-ss-truc">
            {ss.truc.filter((t) => t.truoc != null || t.sau != null).map((t) => (
              <li key={t.ma}><span>{t.ten}</span><span className="so">{t.truoc ?? "?"} → <strong>{t.sau ?? "?"}</strong>/5</span><span className={`bc-ss-chenh nho ${t.sau > t.truoc ? "len" : t.sau < t.truoc ? "xuong" : ""}`}>{chenh(t.truoc, t.sau)}</span></li>
            ))}
          </ul>
        </>
      )}
      <details className="bc-ss-slides">
        <summary>Xem từng slide hai lượt cạnh nhau</summary>
        <p className="nho">Xếp theo số slide. Nếu bạn đã thêm hoặc bớt slide, số slide hai lượt có thể lệch nhau.</p>
        <div className="bc-ss-luoi">
          <span className="bc-nhan-nho">Lượt {truoc.luot || "trước"}</span><span className="bc-nhan-nho">Lượt này</span>
          {ss.slides.map(({ so, truoc: a, sau: b }) => (
            <div key={so} className="bc-ss-hang">
              <div>{a ? <><AnhNho url={urlCu[so]} so={so} /><p><strong>Slide {so}</strong> <span className={`pill bc-pill-${a.danhGia}`}>{DANH_GIA_SLIDE[a.danhGia]}</span></p><p className="nho">{a.nhanXetNgan}</p></> : <p className="nho">Không có slide {so}</p>}</div>
              <div>{b ? <><AnhNho url={url[so]} so={so} /><p><strong>Slide {so}</strong> <span className={`pill bc-pill-${b.danhGia}`}>{DANH_GIA_SLIDE[b.danhGia]}</span></p><p className="nho">{b.nhanXetNgan}</p></> : <p className="nho">Không có slide {so}</p>}</div>
            </div>
          ))}
        </div>
      </details>
      <p><Link to={linkLuot(truoc.id)}>Mở lại toàn bộ nhận xét lượt {truoc.luot || "trước"} →</Link>{truoc.deckDaXoa ? <span className="nho"> (tệp deck lượt này đã tự xoá sau 90 ngày, nhận xét vẫn còn)</span> : null}</p>
    </section>
  );
}
